
import { useState, useEffect } from 'react';

export const usePWADetection = () => {
  const [isInstalled, setIsInstalled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const detectPWA = async () => {
      try {
        // Check for median.co wrapper first (highest priority)
        const isMedianWrapper = window.location.hostname.includes('median.co') || 
                                window.navigator.userAgent.includes('median') ||
                                (window as any).median !== undefined;

        if (isMedianWrapper) {
          setIsInstalled(true);
          setLoading(false);
          return;
        }

        // Check if user explicitly chose browser mode
        const browserMode = localStorage.getItem('gambgo_use_browser_mode') === 'true';
        const urlParams = new URLSearchParams(window.location.search);
        const isBrowserSource = urlParams.get('source') === 'browser';

        if (browserMode || isBrowserSource) {
          setIsInstalled(true); // Treat browser mode as "installed" to show app routes
          setLoading(false);
          return;
        }

        // Method 1: Check if running in standalone mode (PWA installed)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                            (window.navigator as any).standalone === true;

        if (isStandalone) {
          setIsInstalled(true);
          setLoading(false);
          return;
        }

        // Method 2: Check for Android home screen shortcut
        // If added to home screen on Android, it might not be in standalone mode
        const isAndroid = /android/i.test(navigator.userAgent);
        const isAddedToHomeScreen = localStorage.getItem('gambgo_added_to_homescreen') === 'true';
        
        if (isAndroid && isAddedToHomeScreen) {
          setIsInstalled(true);
          setLoading(false);
          return;
        }

        // Method 3: Check using getInstalledRelatedApps (if available)
        if ('getInstalledRelatedApps' in navigator) {
          try {
            const relatedApps = await (navigator as any).getInstalledRelatedApps();
            if (relatedApps && relatedApps.length > 0) {
              setIsInstalled(true);
              setLoading(false);
              return;
            }
          } catch (error) {
            console.log('getInstalledRelatedApps not supported or failed:', error);
          }
        }

        // Method 4: Check for PWA-specific URL parameters or localStorage
        const launchedFromPWA = urlParams.get('source') === 'pwa' || 
                               localStorage.getItem('gambgo_launched_from_pwa') === 'true';

        if (launchedFromPWA) {
          setIsInstalled(true);
          setLoading(false);
          return;
        }

        // If none of the above methods detect installation, assume not installed
        setIsInstalled(false);
        setLoading(false);

      } catch (error) {
        console.error('Error detecting PWA installation:', error);
        setIsInstalled(false);
        setLoading(false);
      }
    };

    detectPWA();
  }, []);

  return { isInstalled, loading };
};
